import { useLayoutEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import type { NavLinkProps } from "react-router-dom";
import {
  ArrowLeftRight,
  CalendarClock,
  CreditCard,
  FileInput,
  LayoutDashboard,
  LogOut,
  Settings,
  BarChart2,
  Table2,
  Users,
  Server,
  ShieldCheck,
  LockKeyhole,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { OrgSwitcher } from "../../features/org-switcher/OrgSwitcher";
import { useLogout } from "../../hooks/useLogout";
import { useSession } from "../../auth/SessionContext";
import { deploymentTargetName, useDeploymentTarget } from "../../features/deployments/DeploymentTargetContext";
import { Feature, featureDecision } from "../../features/feature-gates/FeatureGate";

const NAV_FEATURES = ['deployments.self_hosted', 'app.dashboard', 'flow.module', 'forms.module', 'action_tables.module', 'app.teams'] as const;

function DeploymentNavLink({ to, children, ...props }: NavLinkProps) {
  const { hrefFor, selectedDeployment } = useDeploymentTarget();
  const path = typeof to === 'string' ? to : `${to.pathname ?? ''}${to.search ?? ''}${to.hash ?? ''}`;
  const href = hrefFor(path);
  const inactive = { isActive: false, isPending: false, isTransitioning: false };
  const inactiveChildren = typeof children === 'function' ? children(inactive) : children;
  if (selectedDeployment && !href) {
    const className = typeof props.className === 'function'
      ? props.className(inactive)
      : props.className;
    return <span className={cn(className, 'cursor-not-allowed opacity-50')} aria-disabled="true" title="This deployment needs an application URL before configurations can be opened.">{inactiveChildren}</span>;
  }
  if (href) {
    const resolved = new URL(href, window.location.origin);
    if (resolved.origin !== window.location.origin) {
      const className = typeof props.className === 'function'
        ? props.className(inactive)
        : props.className;
      return <a href={resolved.toString()} className={className}>{inactiveChildren}</a>;
    }
    return <NavLink to={`${resolved.pathname}${resolved.search}${resolved.hash}`} {...props}>{children}</NavLink>;
  }
  return <NavLink to={to} {...props}>{children}</NavLink>;
}

function DeploymentSwitcher() {
  const { deployments, policy, selectedDeployment, loading, error, selectDeployment } = useDeploymentTarget();
  if (loading || (!error && deployments.length === 0)) return null;
  if (error && deployments.length === 0) {
    return <div role="status" className="shrink-0 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900 lg:mb-3">Execution locations unavailable</div>;
  }
  return (
    <div className="shrink-0 rounded-xl border border-gray-200 bg-gray-50 p-2.5 lg:mb-3" aria-label="Execution location">
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.12em] text-gray-500"><Server className="h-3.5 w-3.5" aria-hidden="true" />Execution location</div>
      {policy.selectionLocked ? (
        <div className="mt-1.5 flex min-h-9 items-center gap-2 text-sm font-semibold text-gray-950"><LockKeyhole className="h-4 w-4 text-red-600" aria-hidden="true" /><span className="max-w-40 truncate">{selectedDeployment ? deploymentTargetName(selectedDeployment) : 'Locked default'}</span></div>
      ) : deployments.length === 1 ? (
        <div className="mt-1.5 max-w-44 truncate text-sm font-semibold text-gray-950">{selectedDeployment ? deploymentTargetName(selectedDeployment) : deploymentTargetName(deployments[0]!)}</div>
      ) : (
        <select aria-label="Choose execution location" value={selectedDeployment?.deploymentId ?? ''} onChange={(event) => selectDeployment(event.target.value)} className="mt-1.5 min-h-9 w-44 rounded-lg border border-gray-300 bg-white px-2 text-sm font-semibold text-gray-900 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-200 lg:w-full">
          {deployments.map((deployment) => <option key={deployment.deploymentId} value={deployment.deploymentId}>{deploymentTargetName(deployment)}</option>)}
        </select>
      )}
      {error && <p role="status" className="mt-2 text-[11px] leading-4 text-amber-800">Refresh delayed; showing the last known locations.</p>}
    </div>
  );
}

export function Sidebar() {
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);
  const isFlowActive =
    location.pathname === "/flow" ||
    location.pathname.startsWith("/flow/");
  const { logout, isLoggingOut } = useLogout();
  const { isAppAdmin, features } = useSession();
  // A policy version can change without changing any navigation. Reorient only
  // when the route or visible links change, preserving manual sidebar scroll.
  const navigationPolicy = `${isAppAdmin}:${NAV_FEATURES.map((id) => featureDecision(features, id).enabled).join(',')}`;

  useLayoutEffect(() => {
    const activeLink = navRef.current?.querySelector<HTMLElement>(
      '[aria-current="page"]',
    );
    activeLink?.scrollIntoView?.({ block: "nearest", inline: "center" });
  }, [navigationPolicy, location.pathname]);

  return (
    <aside className="shrink-0 border-b border-gray-200 bg-white lg:flex lg:h-screen lg:w-60 lg:flex-col lg:border-b-0 lg:border-r">
      <div className="flex h-14 items-center justify-between gap-2 px-4">
        <span className="shrink-0 text-lg font-semibold">Grid2</span>
        <OrgSwitcher />
      </div>
      <nav ref={navRef} className="flex gap-1 overflow-x-auto px-2 pb-3 lg:block lg:flex-1 lg:space-y-1 lg:overflow-visible lg:py-4" aria-label="Main navigation">
        <Feature id="deployments.self_hosted">
          <DeploymentSwitcher />
        </Feature>
        {/* Dashboard */}
        <Feature id="app.dashboard">
          <NavLink
            to="/dashboard"
            end
            className={({ isActive }) =>
              cn(
                "flex shrink-0 items-center gap-3 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
              )
            }
          >
            <LayoutDashboard className="h-4 w-4" aria-hidden="true" />
            Dashboard
          </NavLink>
        </Feature>

        {/* Flow with sub-items */}
        <Feature id="flow.module">
          <div>
            <DeploymentNavLink
              to="/flow"
              end
              className={({ isActive }) =>
                cn(
                  "flex shrink-0 items-center gap-3 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-gray-100 text-gray-900"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                )
              }
            >
              <ArrowLeftRight className="h-4 w-4" aria-hidden="true" />
              Flow
            </DeploymentNavLink>

            {/* Sub-items — shown when any /flow route is active */}
            {isFlowActive && (
              <div className="ml-2 mt-0.5 flex gap-1 lg:ml-7 lg:block lg:space-y-0.5">
                <DeploymentNavLink
                  to="/flow"
                  end
                  className={({ isActive }) =>
                    cn(
                      "flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                      isActive
                        ? "text-gray-900 bg-gray-100"
                        : "text-gray-500 hover:text-gray-900 hover:bg-gray-50",
                    )
                  }
                >
                  Flows
                </DeploymentNavLink>
                <DeploymentNavLink
                  to="/flow/dashboard"
                  className={({ isActive }) =>
                    cn(
                      "flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                      isActive
                        ? "text-gray-900 bg-gray-100"
                        : "text-gray-500 hover:text-gray-900 hover:bg-gray-50",
                    )
                  }
                >
                  <BarChart2 className="h-3 w-3" aria-hidden="true" />
                  Dashboard
                </DeploymentNavLink>
                <DeploymentNavLink
                  to="/flow/schedule"
                  className={({ isActive }) =>
                    cn(
                      "flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                      isActive
                        ? "text-gray-900 bg-gray-100"
                        : "text-gray-500 hover:text-gray-900 hover:bg-gray-50",
                    )
                  }
                >
                  <CalendarClock className="h-3 w-3" aria-hidden="true" />
                  Schedule
                </DeploymentNavLink>
              </div>
            )}
          </div>
        </Feature>

        {/* Forms */}
        <Feature id="forms.module">
          <DeploymentNavLink
            to="/forms"
            className={({ isActive }) =>
              cn(
                "flex shrink-0 items-center gap-3 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
              )
            }
          >
            <FileInput className="h-4 w-4" aria-hidden="true" />
            Forms
          </DeploymentNavLink>
        </Feature>

        {/* Action Tables */}
        <Feature id="action_tables.module">
          <DeploymentNavLink
            to="/action-tables"
            className={({ isActive }) =>
              cn(
                "flex shrink-0 items-center gap-3 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
              )
            }
          >
            <Table2 className="h-4 w-4" aria-hidden="true" />
            Action Tables
          </DeploymentNavLink>
        </Feature>

        {/* Teams */}
        <Feature id="app.teams">
          <NavLink
            to="/teams"
            className={({ isActive }) =>
              cn(
                "flex shrink-0 items-center gap-3 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
              )
            }
          >
            <Users className="h-4 w-4" aria-hidden="true" />
            Teams
          </NavLink>
        </Feature>

        {/* Billing */}
        <NavLink
          to="/billing"
          className={({ isActive }) =>
            cn(
              "flex shrink-0 items-center gap-3 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "bg-gray-100 text-gray-900"
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
            )
          }
        >
          <CreditCard className="h-4 w-4" aria-hidden="true" />
          Billing
        </NavLink>

        {isAppAdmin && (
          <Feature id="deployments.self_hosted">
            <NavLink
              to="/deployments"
              className={({ isActive }) =>
                cn(
                  "flex shrink-0 items-center gap-3 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-gray-100 text-gray-900"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                )
              }
            >
              <Server className="h-4 w-4" aria-hidden="true" />
              Deployments
            </NavLink>
          </Feature>
        )}

        {isAppAdmin && (
          <NavLink
            to="/organization-admin"
            className={({ isActive }) =>
              cn(
                "flex shrink-0 items-center gap-3 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
              )
            }
          >
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
            Organization Admin
          </NavLink>
        )}

        {/* Settings */}
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            cn(
              "flex shrink-0 items-center gap-3 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "bg-gray-100 text-gray-900"
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
            )
          }
        >
          <Settings className="h-4 w-4" aria-hidden="true" />
          Settings
        </NavLink>
      </nav>

      {/* Account section — pinned to the bottom on the desktop column
          layout via `mt-auto`; on the mobile/tablet row layout it simply
          follows the nav in normal flow, so logout stays reachable there
          too instead of being clipped or hidden. */}
      <div className="border-t border-gray-100 p-2 lg:mt-auto">
        <button
          type="button"
          onClick={() => void logout()}
          disabled={isLoggingOut}
          aria-label={isLoggingOut ? "Signing out" : "Log out"}
          className="flex w-full items-center gap-3 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-60"
        >
          <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
          {isLoggingOut ? "Signing out…" : "Log out"}
        </button>
      </div>
    </aside>
  );
}
